import * as dagre from '@dagrejs/dagre';
import { Alert, AlertColor, Snackbar } from "@mui/material";
import { cloneDeep } from "lodash";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import ReactFlow, { Controls, Edge, updateEdge } from "reactflow";
import "reactflow/dist/style.css";
import appContext from "../../appContext";
import CustomizedSingleSelectDropdown from "../CreateForm/DropdownBase";
import { PrimaryButton, SecondaryButton } from "../Custom/Buttons";
import CustomMuiBackDrop from "../Custom/CustomMuiBackDrop";
import FlowChangeConfirmationModal from "../Custom/Modals/FlowChangeConfirmationModal";
import CustomEdge from "./CustomEdge";
import CustomNode from "./CustomNode";
import {
  FlowBuilderContainer,
  LogicButtonContainer,
  SpaceWrapper,
  StyledCondition,
  StyledEdgeContainer,
  StyledEdgeContent,
  StyledEdgeDropdownContainer,
  StyledEdgePopupContainer,
  StyledEdgeTitle,
} from "./styled";
import { getFlowData, logicalTypes } from "./utils";

const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

const nodeWidth = 150;
const nodeHeight = 150;

const getLayoutedElements = (
  nodes: any,
  edges: any,
  direction: string = "LR"
) => {
  const isHorizontal = direction === "LR";
  dagreGraph.setGraph({ rankdir: direction });
  dagreGraph.setGraph({
    nodesep: 300,
    ranksep: 300,
    rankdir: direction,
  });
  nodes.forEach((node: any) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach((edge: Edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  nodes.forEach((node: any) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    node.targetPosition = isHorizontal ? "left" : "top";
    node.sourcePosition = isHorizontal ? "right" : "bottom";

    node.position = {
      x: nodeWithPosition.x - nodeWidth / 2,
      y: nodeWithPosition.y - nodeHeight / 2,
    };

    return node;
  });

  return { nodes, edges };
};

export default function FlowBuilder({
  nodes,
  edges,
  setNodes,
  setEdges,
  onNodesChange,
  onEdgesChange,
  setSelectedNodeId,
  expandSidebar,
  allQuestionFormData,
  saveHandler,
  formDirty,
  setFormDirty,
  selectedNodeId,
  setNewQuestionData,
  setQuestionFormData
}: any) {
  const { triggerRemoveLogic, setTriggerRemoveLogic } = useContext(appContext)
  const [tempNodeId, setTempNodeId] = useState("");
  const [popupState, setPopupState] = useState(false);
  const [selectedEdge, setSelectedEdge] = useState<any>(null);
  const [edgeOptions, setEdgeOptions] = useState<any>([])
  const [toastr, setToastr] = useState<any>(false);
  const [errorType, setErrorType] = useState<AlertColor>("error");
  const [errorMessage, setErrorMessage] = useState<string>("Only one edge can be created from this handle.");
  const [selectedTargetValue, setSelectedTargetValue] = useState("")

  const onConnect = useCallback(
    (params: any, updatedEdge?: Edge[], updatedQuestionData?: any) => {
      let currentEdge = [...edges]
      if (updatedEdge?.length) {
        currentEdge = [...updatedEdge]
      }
      let currentQuestionData = [...allQuestionFormData]
      if (updatedQuestionData) {
        currentQuestionData = [...updatedQuestionData]
      }

      const isSourceUsed = currentEdge.some((edge: Edge) => {
        const isSameSource = edge.source === params.source;
        const isSameHandle = edge.sourceHandle === params.sourceHandle;
        const isSameTarget = ((edge.target === params.target) && (params.target !== 'end'));
        const isNextLabel = edge.data?.label === 'next';

        // Only block if it's same source + same handle + same target OR label is next
        return (
          isSameSource &&
          ((isSameHandle && isSameTarget) || isNextLabel)
        );
      });

      if (isSourceUsed) {
        setErrorType('error')
        setErrorMessage('Only one edge can be created from this handle.');
        setToastr(true)
        return;
      }

      const currentLabel = edgeOptions.find((option: any) =>
        Object.keys(option).includes(params.sourceHandle)
      )?.[params.sourceHandle];
      const sourceQuestion = currentQuestionData.find((question: any) => question?.name === params?.source)

      const currentOption = sourceQuestion?.options.findIndex((option: any) => option.id === params?.sourceHandle)
      const newEdge = {
        ...params,
        markerEnd: { type: 'arrowclosed' },
        data: {
          label: currentLabel || 'next',
          sourceName: currentQuestionData.find((question: any) => question.name === params.source)?.title,
          targetName: currentQuestionData.find((question: any) => question.name === params.target)?.title,
          sourceIndex: currentQuestionData.findIndex((question: any) => question.name === params.source),
          targetIndex: currentQuestionData.findIndex((question: any) => question.name === params.target),
          totalOptionCount: sourceQuestion?.options?.length ? sourceQuestion?.options?.length : 1,
          currentOption: currentOption ? currentOption + 1 : 1
        },
        id: `edge-${params.source}-${params.target}`,
        type: "custom",
      };
      setEdges((prevEdges: any) => [...prevEdges, newEdge]);
    },
    [setEdges, edgeOptions, edges, allQuestionFormData]
  );

  const getDependedQuestion = (currentDependedId: any, prevDependedId: any) => {
    // Convert inputs to arrays for uniform comparison
    const currentIds = Array.isArray(currentDependedId) ? currentDependedId : [currentDependedId];
    const prevIds = Array.isArray(prevDependedId) ? prevDependedId : [prevDependedId];

    // Check if all prevIds are in currentIds
    return prevIds.some((prevId) => currentIds.includes(prevId));
  };


  const handleEdgeChange = () => {
    if (selectedTargetValue) {
      const currentEdge = edges.find((edge: any) => edge?.data?.label === selectedEdge?.label && edge?.data?.sourceName === selectedEdge?.sourceName)
      handleDeleteEdge([currentEdge], true, selectedTargetValue)
      closePopup()
    }
  }

  const getTarget = (questions: any, currentIndex: number, option: any, islogic: boolean) => {

    let filteredQuestion = questions.filter((question: any, index: number) => {
      return option?.id && (Array.isArray(question.dependedId) ? question.dependedId.includes(option.id) : question.dependedId === option.id) && (index !== currentIndex)
    })

    if (!filteredQuestion.length) {
      filteredQuestion = questions.filter((question: any, index: number) => {
        return question?.dependedId?.length && questions[currentIndex]?.dependedId?.length && index > currentIndex && getDependedQuestion(questions[currentIndex]?.dependedId, question.dependedId) && !islogic
      })
    }

    if (!filteredQuestion.length) {
      filteredQuestion = questions.filter((question: any, index: number) => {
        return !question.isDepended && index > currentIndex
      })
    }
    if (!filteredQuestion.length) {
      filteredQuestion = questions.filter((question: any, index: number) => {
        return !question.dependedId?.length && !question.isDepended && index > currentIndex
      })
    }
    return filteredQuestion.length ? filteredQuestion?.[0] : "end"
  }

  const positionNodes = (
    nodes: Node[],
    edges: Edge[],
    xSpacing = 450,
    ySpacing = 350
  ): Node[] => {
    const levels: Record<string, number> = {}; // To group nodes by their hierarchical levels

    // Organize nodes into levels based on edges
    edges.forEach(({ source, target }) => {
      if (!levels[source]) levels[source] = 0; // Root node is level 0
      levels[target] = levels[source] + 1; // Child node is one level deeper
    });

    // Assign positions to nodes
    return nodes.map((node: any, nodeIndex: number) => {
      const level = levels[node.id] || 0;
      const nodesAtLevel = nodes.filter((n: any) => (levels[n.id] || 0) === level);

      const index = nodesAtLevel.findIndex((n: any) => n.id === node.id);

      return {
        ...node,
        position: {
          x: nodeIndex * xSpacing, // Horizontal position based on level
          y: index * ySpacing, // Vertical spacing between nodes at the same level
        },
      };
    });
  };


  const gotoDropdownDataProvider = useMemo(() => {

    const gotoOptions = []
    nodes
      .filter(
        (node: any) =>
          node.customNodeIndex !== selectedNodeId && node.id !== "end"
      )
      .map((node: any, index: number) => {
        gotoOptions.push({
          label: node.data.label,
          value: node.questionData.title
        })
      });

    gotoOptions.push({ label: "End", value: "end" });

    return gotoOptions;
  }, [nodes, selectedNodeId, selectedEdge]);

  const generateEdges = (questions: any) => {
    const edgeArray: any = []
    questions.forEach((question: any, index: number) => {
      if (logicalTypes.includes(question.type)) {

        question.options.forEach((option: any, optionIndex: number) => {
          const currentTarget = getTarget(questions, index, option, true)
          edgeArray.push({
            id: option.label
              ? `edge-${question.name}-${currentTarget === "end" ? currentTarget : currentTarget.name}-${option.label}` : `edge-${question.name}-${currentTarget === "end" ? currentTarget : currentTarget.name}`,
            source: question.name,
            target: currentTarget === "end" ? currentTarget : currentTarget.name,
            type: "custom",
            markerEnd: { type: 'arrowclosed' },
            data: {
              label: option.label,
              sourceName: question.title,
              targetName: currentTarget === "end" ? currentTarget : currentTarget.title,
              sourceIndex: index,
              targetIndex: questions.findIndex((innerQuestion: any) => innerQuestion.name === currentTarget?.name),
              totalOptionCount: question?.options?.length ? question?.options?.length : 1,
              currentOption: optionIndex + 1
            },
            sourceHandle: option?.id
          })
        })
      } else {
        const currentTarget = getTarget(questions, index, {}, false)
        edgeArray.push({
          id: `edge-${question.name}-${currentTarget === "end" ? currentTarget : currentTarget.name}`,
          source: question.name,
          target: currentTarget === "end" ? currentTarget : currentTarget.name,
          type: "custom",
          markerEnd: { type: 'arrowclosed' },
          data: {
            label: "next",
            sourceName: question.title,
            targetName: currentTarget === "end" ? currentTarget : currentTarget.title,
            sourceIndex: index,
            targetIndex: questions.findIndex((innerQuestion: any) => innerQuestion.name === currentTarget?.name)
          },
          sourceHandle: currentTarget.name
        })
      }
    })
    return edgeArray
  }
  useEffect(() => {

    const flowData = getFlowData(allQuestionFormData);
    const { nodes: layoutedNodes } = getLayoutedElements(
      flowData.nodes.map((node: any, index: number) => ({
        ...node,
        data: {
          ...node.data,
          questionData: allQuestionFormData.find(
            (question: any) => question.name === node.id
          ),
          options: flowData.edgeOptions[index],
        },
      })),
      flowData.edges
    );

    setEdgeOptions(flowData.edgeOptions);
    const generatedEdges = generateEdges(allQuestionFormData)
    setEdges(generatedEdges);
    setNodes(positionNodes(layoutedNodes, generatedEdges));

    //for future use case
    // setEdges((prevEdges: any) =>
    //   prevEdges.length > 0 ? prevEdges : layoutedEdges
    // );
  }, [setNodes, setEdges]);

  useEffect(() => {
    if (triggerRemoveLogic) {
      const allQuestionFormDataClone = cloneDeep(allQuestionFormData)
      const updatedQuestions = allQuestionFormDataClone.map((question: any) => {

        question.isDepended = false
        question.dependedId = ""
        if (question.dependedType) {
          delete question.dependedType
        }
        return question
      })
      setQuestionFormData(updatedQuestions)
      const generatedEdges = generateEdges(updatedQuestions)
      setEdges(generatedEdges);
      setNodes((prev: any) => (positionNodes(prev, generatedEdges)));
      setTriggerRemoveLogic(false)
    }
  }, [triggerRemoveLogic])

  const nodeTypes = useMemo(() => ({ customNode: CustomNode }), [edgeOptions]);
  const edgeTypes = useMemo(() => ({ custom: CustomEdge }), [edgeOptions, edges]);

  const onEdgeUpdate = useCallback(
    (oldEdge: any, newConnection: any) =>
      setEdges((els: any) => updateEdge(oldEdge, newConnection, els)),
    [setEdges]
  );

  const onEdgeClick = (edge: any) => {
    //for future use case
    if (!edge?.data?.targetName && edge?.target === "end") {
      edge.data.targetName = "end"
    }
    setSelectedEdge(edge?.data);
  };

  const closePopup = () => {
    setSelectedEdge("")
    setSelectedEdge(null);
  };

  function getTracksWithLabels(edges: any[]) {
    // Filter edges that have a label
    const labeledEdges = edges.filter((edge) => edge?.data?.label);

    // Function to build the track for a specific sourceHandle
    const buildTrack = (sourceHandle: string) => {
      const track: string[] = [];
      let currentEdge = labeledEdges.find((edge) => edge.sourceHandle === sourceHandle);

      let breakLoop = false;
      while (currentEdge) {
        track.push(currentEdge.target);
        if (breakLoop) {
          break
        }

        if (allQuestionFormData.filter((question: any) => currentEdge?.target === question.name && logicalTypes.includes(question.type)).length) {
          if (allQuestionFormData.filter((question: any) => currentEdge?.source === question.name && logicalTypes.includes(question.type)).length || track.length > 1) {
            break
          }
          breakLoop = true
        }
        currentEdge = labeledEdges.find((edge) => edge.source === currentEdge.target);
      }

      return track
    };

    // Extract unique sourceHandles only from labeled edges
    const uniqueSourceHandles = Array.from(
      new Set(labeledEdges.map((edge) => edge.sourceHandle).filter(Boolean))
    );

    // Build the result for each unique sourceHandle
    const result = uniqueSourceHandles.map((sourceHandle) => ({
      sourceHandle,
      track: buildTrack(sourceHandle),
    }));

    const nonLabelHandlers = edges.filter((edge) => edge?.data?.label !== "next")?.map((edge) => edge?.sourceHandle)
    return result.filter((res) => nonLabelHandlers.includes(res?.sourceHandle));
  }

  const getIsLoop = (allQuestions: any, index: number, currentTrack: any) => {
    const optionIndex = allQuestions.findIndex((question: any) =>
      question?.options?.some((option: any) => option.id === currentTrack?.sourceHandle)
    );
    if (index <= optionIndex) {
      return { isLooping: true, start: index, end: optionIndex }
    }
    return { isLooping: false, start: 0, end: 0 }
  }

  function updateDependencyStatus(
    track: { sourceHandle: string; track: string[] }[],
    allQuestionFormDataClone: any[],
    edges: { source: string; sourceHandle: string | null; target: string; data?: { label?: string }; }[]
  ) {
    const newQuestionData = cloneDeep(allQuestionFormDataClone)
    // Add or update dependency status based on the track
    track.forEach((eachTrack) => {
      newQuestionData.forEach((eachQuestion: any, index: number) => {
        if (eachTrack?.track?.includes(eachQuestion.name)) {
          const loopStatus = getIsLoop(newQuestionData, index, eachTrack)

          if (loopStatus.isLooping) {
            newQuestionData.forEach((innerEachQuestion: any, innerIndex: number) => {
              if (innerIndex >= loopStatus.start && innerIndex <= loopStatus.end) {
                if (!Array.isArray(innerEachQuestion.dependedId)) {
                  innerEachQuestion.dependedId = [];
                }
                if (!innerEachQuestion.dependedId.includes(eachTrack.sourceHandle)) {
                  innerEachQuestion.dependedId.push(eachTrack.sourceHandle);
                }
              }
            })

          } else {
            if (index > 0) {
              eachQuestion.isDepended = true;
            }
            // Initialize dependedId as an array if not already
            if (!Array.isArray(eachQuestion.dependedId)) {
              eachQuestion.dependedId = [];
            }

            // Add sourceHandle only if it's not already present
            if (!eachQuestion.dependedId.includes(eachTrack.sourceHandle)) {
              eachQuestion.dependedId.push(eachTrack.sourceHandle);
            }
          }
        }
      });
    });

    // Remove dependencies that are not part of any current track
    newQuestionData.forEach((eachQuestion: any) => {
      if (Array.isArray(eachQuestion.dependedId)) {
        // Filter out sourceHandles that are no longer in any track
        eachQuestion.dependedId = eachQuestion.dependedId.filter((sourceHandle: string) => {
          return track.some((eachTrack) => eachTrack.sourceHandle === sourceHandle);
        });

        // If no dependencies remain, reset isDepended
        if (eachQuestion.dependedId.length === 0) {
          eachQuestion.isDepended = false;
          delete eachQuestion.dependedId;
        }
      }
    });
    setQuestionFormData(newQuestionData)
  }

  const handleDeleteEdge = (deletedEdge: Edge[], isFromPopup?: boolean, selectedValue?: string) => {

    let allQuestionFormDataClone: any = [...allQuestionFormData];

    deletedEdge.forEach((edge: any) => {
      if (!edge) return;

      const currentSourceQuestion = allQuestionFormDataClone.filter(
        (question: any) => question.name === edge.source
      );

      // if (edge.target === "end") return;
      let isAlreadyIn = false
      allQuestionFormDataClone.forEach((question: any) => {
        if (question.name === edge.target || isAlreadyIn) {
          isAlreadyIn = true
          const sourceDependedId = currentSourceQuestion?.[0]?.dependedId;
          const isSourceDependedIdArray = Array.isArray(sourceDependedId);

          question["dependedId"] = Array.isArray(question["dependedId"])
            ? question["dependedId"].filter((dependedId: any) => {
              return isSourceDependedIdArray
                ? (!sourceDependedId.includes(dependedId) && edge.sourceHandle !== dependedId)
                : (sourceDependedId !== dependedId && edge.sourceHandle !== dependedId)
            })
            : "";
        }
      });
      const edgeClone: Edge[] = edges.filter(
        (filterEdge: Edge) =>
          !(filterEdge.source === edge.source && filterEdge.data.label === edge.data.label)
      )
      setEdges(edgeClone);
      setQuestionFormData(allQuestionFormDataClone);
      if (isFromPopup) {
        const sourceQuestion = allQuestionFormDataClone.find((question: any) => question.title === selectedEdge?.sourceName)
        const targetQuestion = allQuestionFormDataClone.find((question: any) => question.title === selectedValue)
        onConnect({
          "source": sourceQuestion?.name,
          "sourceHandle": sourceQuestion?.options?.find((option: any) => option.label === selectedEdge?.label)?.id,
          "target": targetQuestion?.name || 'end',
          "targetHandle": null
        },
          edgeClone,
          allQuestionFormDataClone
        )
      }
    });
  };

  useEffect(() => {
    if (edges.length === 0) return

    const allQuestionFormDataClone: any = cloneDeep(allQuestionFormData)
    const track = getTracksWithLabels(edges)
    updateDependencyStatus(track, allQuestionFormDataClone, edges);
    setNewQuestionData(allQuestionFormDataClone)
  }, [edges])

  const renderEdgePopupSection = (edgeDetails: any) => {
    let tempValue = []
    if (edgeDetails?.label === "next") {
      for (let i = 0; i < gotoDropdownDataProvider.length; i++) {
        if (i > edgeDetails?.sourceIndex) {
          tempValue.push(gotoDropdownDataProvider[i])
        }
      }
    }
    return (
      <StyledEdgePopupContainer>
        <StyledEdgeTitle>Branching Details </StyledEdgeTitle>
        <StyledEdgeContainer>
          <StyledEdgeContent>
            <StyledCondition>If</StyledCondition>
            <SpaceWrapper>
              {edgeDetails?.sourceName} is "{edgeDetails?.label}"
            </SpaceWrapper>
          </StyledEdgeContent>
          <StyledEdgeDropdownContainer>
            <StyledCondition>then go to</StyledCondition>
            <CustomizedSingleSelectDropdown
              fullWidth
              dataProvider={tempValue.length > 0 ? tempValue : gotoDropdownDataProvider}
              value={edgeDetails?.targetName}
              setValue={(value) => {
                setSelectedTargetValue(value)
                setSelectedEdge((prev: Edge) => ({ ...prev, targetName: value }))
              }}
            />
          </StyledEdgeDropdownContainer>
        </StyledEdgeContainer>
        <LogicButtonContainer>
          <SecondaryButton style={{ width: "40%" }} onClick={closePopup}>
            Close
          </SecondaryButton>
          <PrimaryButton style={{ width: "40%" }} onClick={handleEdgeChange}>
            Save
          </PrimaryButton>
        </LogicButtonContainer>
      </StyledEdgePopupContainer>
    );
  };

  return (
    <React.Fragment>
      <FlowBuilderContainer>
        <ReactFlow
          fitView
          nodes={nodes}
          edges={edges}
          onConnect={onConnect}
          onEdgesDelete={handleDeleteEdge}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          edgeTypes={edgeTypes}
          nodeTypes={nodeTypes}
          onNodeClick={(event: any, node: any) => {
            if (node.id === "end") {
              return;
            }

            if (formDirty) {
              setTempNodeId(node?.customNodeIndex);
            } else {
              setSelectedNodeId(node?.customNodeIndex);
            }
          }}
          onEdgeUpdate={onEdgeUpdate}
          onEdgeClick={(event: any, edge: any) => {
            const currentSource = allQuestionFormData.filter((question: any) => question.name === edge.source)
            if (edge?.data?.label === "next" && !currentSource?.[0]?.isDepended && !currentSource?.[0]?.dependedId?.length) {
              setEdges((prevEdges: any) =>
                prevEdges.map((e: any) =>
                  e.id === edge.id ? { ...e, selected: false } : e
                )
              );
              setErrorType('error')
              setErrorMessage('Cannot modify non depended questions');
              setToastr(true)
              return;
            }
            onEdgeClick(edge);
          }}
        >
          <Controls />
        </ReactFlow>
        {selectedEdge && renderEdgePopupSection(selectedEdge)}
        {selectedEdge && <CustomMuiBackDrop loading={false} />}
      </FlowBuilderContainer>
      <Snackbar
        open={toastr}
        autoHideDuration={3000}
        onClose={() => setToastr(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert variant="filled" onClose={() => setToastr(false)} severity={errorType}>
          {errorMessage}
        </Alert>
      </Snackbar>
      <FlowChangeConfirmationModal
        isOpen={popupState}
        cancelHandler={() => {
          // expandSidebar();
          setPopupState(false);
          setSelectedNodeId(tempNodeId);
          setTempNodeId("");
          setFormDirty(false);
          setNewQuestionData(allQuestionFormData);
        }}
        submitHandler={() => {
          saveHandler(true);
          setTimeout(() => {
            // expandSidebar();
            setPopupState(false);
            setSelectedNodeId(tempNodeId);
            setTempNodeId("");
          }, 100);
        }}
      />
    </React.Fragment>
  );
}
